/**
 * BM-H4390 — Finance → Invoices.
 *
 * May (accounts) asked for invoicing under her Finance menu. Native client
 * invoices were reachable only by opening a fee earner's Invoice Request
 * from the Invoice Tracker, and nothing listed the invoices themselves.
 *
 * This page lists them (GET /api/invoices). It deliberately does not add a
 * second way to create or issue one: Vanessa, 26 Sep 2026, "there is a
 * working invoice request page which works well, use it". New Invoice goes
 * to that form, and a row made from a request opens the request, where the
 * existing native invoice editor drafts, approves and issues it.
 *
 * BM-H4554 (20 Oct 2026, May Ann): accounts and directors can delete an
 * unissued draft (duplicates made by pressing Generate more than once).
 *
 * 27 Oct 2026 (May Ann): New Invoice offers "Disbursements only", the
 * short invoice request (Vanessa chose that over a request-less draft, so
 * the tracker, filing and credit control still follow).
 * Issued and void invoices keep their record; the draft a live invoice
 * request is using is refused by the server, and its message is shown.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHero from "../../components/PageHero";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ChevronDown, Download, FileText, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { invoiceFileName } from "../../components/NativeInvoiceDraft";
import { API } from "../../config/api";
import { getUserRole, ROLES } from "../../config/roleConfig";
import { errorMessage } from "../../utils/errorMessage";

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") || "";
  return { "X-User-Email": email };
}

export function canDeleteDraftInvoice(email) {
  return [ROLES.DIRECTOR, ROLES.ACCOUNTS].includes(getUserRole(email));
}

function fmtGbp(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB");
}

const FILTERS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Drafts" },
  { key: "issued", label: "Issued" },
  { key: "void", label: "Void" },
];

const STATUS_STYLE = {
  draft: "text-status-amber",
  issued: "text-status-green",
  void: "text-txt-muted line-through",
};

export default function ClientInvoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newMenu, setNewMenu] = useState(false);
  const [filter, setFilter] = useState("all");
  const [downloading, setDownloading] = useState("");
  const [confirmDelete, setConfirmDelete] = useState("");
  const [deleting, setDeleting] = useState("");
  const canDelete = canDeleteDraftInvoice(authHeaders()["X-User-Email"]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await axios.get(`${API}/invoices`, { headers: authHeaders() });
      setInvoices(resp.data?.invoices || []);
    } catch (err) {
      // A failed read is never shown as "no invoices" (AGENTS.md rule 4).
      setInvoices([]);
      setError("Could not load invoices. Refresh to try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(
    () => (filter === "all" ? invoices : invoices.filter((i) => i.status === filter)),
    [invoices, filter],
  );

  const downloadPdf = async (inv, e) => {
    e.stopPropagation();
    setDownloading(inv.id);
    try {
      const resp = await axios.get(`${API}/invoices/${inv.id}/pdf`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = invoiceFileName(inv.matter_ref, inv.invoice_number);
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Could not download the PDF");
    } finally {
      setDownloading("");
    }
  };

  // First press arms, second press deletes: one confirm, no modal.
  const deleteDraft = async (inv, e) => {
    e.stopPropagation();
    if (confirmDelete !== inv.id) {
      setConfirmDelete(inv.id);
      return;
    }
    setDeleting(inv.id);
    try {
      await axios.delete(`${API}/invoices/${inv.id}`, { headers: authHeaders() });
      setInvoices((prev) => prev.filter((i) => i.id !== inv.id));
      toast.success(`Draft on ${inv.matter_ref} deleted`);
    } catch (err) {
      toast.error(errorMessage(err, "Could not delete the draft"));
    } finally {
      setDeleting("");
      setConfirmDelete("");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="mb-2 -ml-2 text-xs text-slate-400">
        <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Dashboard
      </Button>
      {/* 27 Oct 2026 system-wide refresh: header band; the list keeps its density. */}
      <PageHero icon={FileText} title="Invoices" subtitle="Drafts, issued and void invoices, newest first" testId="client-invoices-hero">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} title="Refresh" data-testid="client-invoices-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <div className="relative">
            <Button size="sm" onClick={() => setNewMenu((v) => !v)} aria-expanded={newMenu} data-testid="client-invoices-new">
              <Plus className="w-4 h-4 mr-1" /> New Invoice <ChevronDown className="w-3.5 h-3.5 ml-1" />
            </Button>
            {newMenu && (
              <div className="glass-card absolute right-0 mt-1 w-64 z-20 rounded-md shadow-lg text-sm" data-testid="client-invoices-new-menu">
                <button type="button" className="block w-full text-left px-3 py-2 hover:bg-white/5" onClick={() => navigate("/invoice-request")} data-testid="client-invoices-new-request">
                  Invoice request<span className="block text-xs text-txt-muted">The full form</span>
                </button>
                <button type="button" className="block w-full text-left px-3 py-2 border-t border-white/5 hover:bg-white/5" onClick={() => navigate("/invoice-request?basis=disbursements")} data-testid="client-invoices-new-disb">
                  Disbursements only<span className="block text-xs text-txt-muted">Pick the matter; its unbilled disbursements fill in</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </PageHero>

      <div className="flex gap-2 mb-4">
        {FILTERS.map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={filter === f.key ? "default" : "outline"}
            onClick={() => setFilter(f.key)}
            data-testid={`client-invoices-filter-${f.key}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-txt-muted"><Loader2 className="w-4 h-4 animate-spin" /> Loading invoices</div>
      ) : error ? (
        <div className="text-status-red text-sm" data-testid="client-invoices-error">{error}</div>
      ) : shown.length === 0 ? (
        <div className="text-txt-muted text-sm" data-testid="client-invoices-empty">
          No invoices yet. Use New Invoice to start one from a matter.
        </div>
      ) : (
        <div className="glass-card rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-txt-muted text-xs uppercase tracking-wider">
                <th className="p-3">Number</th>
                <th className="p-3">Date</th>
                <th className="p-3">Matter</th>
                <th className="p-3">Client</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {shown.map((inv) => (
                <tr
                  key={inv.id}
                  data-testid="client-invoice-row"
                  className={`border-t border-white/5 ${inv.source_ref ? "cursor-pointer hover:bg-white/5" : ""}`}
                  onClick={() => inv.source_ref && navigate(`/submission/${inv.source_ref}`)}
                  title={inv.source_ref ? "Open the invoice request" : ""}
                >
                  <td className="p-3 font-medium">{inv.invoice_number || "Draft"}</td>
                  <td className="p-3">{fmtDate(inv.invoice_date)}</td>
                  <td className="p-3 font-mono text-xs">{inv.matter_ref}</td>
                  <td className="p-3">{inv.client_display_name}</td>
                  <td className="p-3 text-right">{fmtGbp(inv.totals?.total_due)}</td>
                  <td className={`p-3 capitalize ${STATUS_STYLE[inv.status] || ""}`}>{inv.status}</td>
                  <td className="p-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => downloadPdf(inv, e)}
                      disabled={downloading === inv.id}
                      title="Download PDF"
                      data-testid="client-invoice-pdf"
                    >
                      {downloading === inv.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </Button>
                    {canDelete && inv.status === "draft" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => deleteDraft(inv, e)}
                        disabled={deleting === inv.id}
                        title={confirmDelete === inv.id ? "Press again to delete this draft" : "Delete draft"}
                        className={confirmDelete === inv.id ? "text-status-red" : "text-txt-muted hover:text-status-red"}
                        data-testid="client-invoice-delete"
                      >
                        {deleting === inv.id ? <Loader2 className="w-4 h-4 animate-spin" />
                          : confirmDelete === inv.id ? <span className="text-xs">Delete?</span> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
